/*
 * Keeps bundle gifts honest in the cart. Gift lines added by the bundle offer
 * carry a hidden `_bundle_gift` property of "<bundle product id>:<min units>".
 * After any cart change, a gift line is reduced to 1 while the customer still
 * has at least <min units> of the bundle product, and removed otherwise, so
 * nobody is charged for a gift they no longer qualify for.
 */
(() => {
  if (window.__bundleCartGuard) {
    return;
  }
  window.__bundleCartGuard = true;

  const routesRoot = () => window.Shopify?.routes?.root || '/';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;
  let timer = null;
  let checking = false;
  let attempts = 0;

  const isCartMutation = (input) => {
    try {
      const url = input instanceof Request ? input.url : String(input);
      return CART_MUTATION.test(new URL(url, window.location.href).pathname);
    } catch {
      return false;
    }
  };

  const schedule = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(check, 400);
  };

  // Wrap whatever fetch is current (cart drawer apps such as UpCart patch it too).
  const previousFetch = window.fetch;
  window.fetch = function (input, init) {
    const result = previousFetch.apply(this, arguments);
    if (isCartMutation(input)) {
      result.then(schedule, () => {});
    }
    return result;
  };

  const open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__bundleCartMutation = isCartMutation(url);
    return open.apply(this, arguments);
  };
  const send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__bundleCartMutation) {
      this.addEventListener('loadend', schedule);
    }
    return send.apply(this, arguments);
  };

  async function check() {
    if (checking) {
      schedule();
      return;
    }
    checking = true;
    try {
      const cart = await (await window.fetch(`${routesRoot()}cart.js`)).json();

      const units = new Map();
      for (const item of cart.items) {
        if (!item.properties?._bundle_gift) {
          const id = String(item.product_id);
          units.set(id, (units.get(id) ?? 0) + item.quantity);
        }
      }

      const allowance = new Map();
      const updates = {};
      for (const item of cart.items) {
        const tag = item.properties?._bundle_gift;
        if (!tag) {
          continue;
        }
        const [bundleProductId, minUnits] = String(tag).split(':');
        const key = `${bundleProductId}|${item.product_id}`;
        if (!allowance.has(key)) {
          allowance.set(key, (units.get(bundleProductId) ?? 0) >= Number(minUnits) ? 1 : 0);
        }
        const allowed = Math.min(item.quantity, allowance.get(key));
        allowance.set(key, allowance.get(key) - allowed);
        if (allowed !== item.quantity) {
          updates[item.key] = allowed;
        }
      }

      if (Object.keys(updates).length === 0) {
        attempts = 0;
        return;
      }
      if (++attempts > 2) {
        return;
      }

      const response = await window.fetch(`${routesRoot()}cart/update.js`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({updates}),
      });
      if (!response.ok) {
        return;
      }
      if (/\/cart\/?$/.test(window.location.pathname)) {
        window.location.reload();
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true}));
    } catch (error) {
      console.warn('[bundle-cart-guard]', error);
    } finally {
      checking = false;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
